// src/pages/AboutUs.tsx
import { Award, Users } from 'lucide-react';

const AboutUs = () => {
  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="page-hero">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/assets/images/o-nas/page-hero.jpg')" }}
        ></div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <div className="hero-content max-w-2xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">O nas</h1>
            <p className="text-xl">Spoznajte naše podjetje, našo zgodbo in našo ekipo.</p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-center mb-12 after:left-1/2 after:-translate-x-1/2">Naša zgodba</h2>
          <div className="flex flex-col md:flex-row items-start gap-8 mb-16">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <img 
                src="/assets/images/o-nas/nasa-zgodba/nasa-delavnica.jpg" 
                alt="Naša delavnica" 
                className="rounded-xl shadow-md w-full h-auto transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue border border-gray-100 card-glow"
              />
            </div>
            <div className="md:w-1/2">
              <div className="bg-white rounded-xl shadow-md p-6 transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue border border-gray-100 card-glow">
                <p className="mb-4">Marca 1996 leta se je Milanu Ačku izpolnila želja, ki jo je gojil že od malih nog, da bi imel nekoč svojo avtomehanično delavnico. Začel je v manjši delavnici z enim dvigalom.</p>
                <p className="mb-4">Po petih letih smo zgradili novo sodobno delavnico in razširili ponudbo naših storitev. Danes je poleg enega redno zaposlenega delavca v novozgrajeni delavnici tudi že sin Klemen.</p>
                <p>Kot zastopniki za servis BOSCH popravljamo vozila vseh znamk. Z najsodobnejšo opremo in diagnostiko skrbimo za vaše vozilo, ne glede na znamko, tip in letnik.</p>
              </div>
            </div>
          </div>

          {/* Timeline Section */}
          <h2 className="section-title text-center mb-12 after:left-1/2 after:-translate-x-1/2">Naš razvoj skozi čas</h2>
          <div className="max-w-3xl mx-auto">
            <div className="timeline-item" style={{ animationDelay: '0.1s' }}>
              <div className="timeline-dot"></div>
              <h3 className="text-xl font-bold mb-2">1996</h3>
              <p>Ustanovitev podjetja. Milan Ačko začne z manjšo delavnici z enim dvigalom.</p>
            </div>
            <div className="timeline-item" style={{ animationDelay: '0.2s' }}>
              <div className="timeline-dot"></div>
              <h3 className="text-xl font-bold mb-2">1998</h3>
              <p>Nabava prvega testerja za diagnostiko vozil (priklop avtomobila na računalnik za odkrivanje napak).</p>
            </div>
            <div className="timeline-item" style={{ animationDelay: '0.3s' }}>
              <div className="timeline-dot"></div>
              <h3 className="text-xl font-bold mb-2">2001</h3>
              <p>Izgradnja nove, večje delavnice s sodobno opremo.</p>
            </div>
            <div className="timeline-item" style={{ animationDelay: '0.4s' }}>
              <div className="timeline-dot"></div>
              <h3 className="text-xl font-bold mb-2">2002</h3>
              <p>Pridružitev servisni mreži Bosch car servis kot eden prvih v Sloveniji.</p>
            </div>
            <div className="timeline-item" style={{ animationDelay: '0.5s' }}>
              <div className="timeline-dot"></div>
              <h3 className="text-xl font-bold mb-2">2005</h3>
              <p>Milan Ačko opravi mojstrski izpit. Širitev ekipe in znanja.</p>
            </div>
            <div className="timeline-item" style={{ animationDelay: '0.6s' }}>
              <div className="timeline-dot"></div>
              <h3 className="text-xl font-bold mb-2">Danes</h3>
              <p>Družinsko podjetje z izkušeno ekipo, ki sledi najnovejšim tehnologijam v avtomobilski industriji.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-autoGray">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-center mb-12 after:left-1/2 after:-translate-x-1/2">Naša ekipa</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue border border-gray-100 card-glow group">
              <div className="overflow-hidden">
                <img 
                  src="/assets/images/o-nas/naša-ekipa/milan-acko.jpg" 
                  alt="Milan Ačko" 
                  className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">Milan Ačko</h3>
                <p className="text-autoBlue mb-4">Ustanovitelj in mojster</p>
                <p>Z več kot 25 let izkušenj v avtomobilski industriji vodi naše podjetje. Leta 2005 je opravil mojstrski izpit.</p>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue border border-gray-100 card-glow group">
              <div className="overflow-hidden">
                <img 
                  src="/assets/images/o-nas/naša-ekipa/klemen-acko.jpg" 
                  alt="Klemen Ačko" 
                  className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">Klemen Ačko</h3>
                <p className="text-autoBlue mb-4">Avtomehanik</p>
                <p>Sin Milana Ačka, ki nadaljuje družinsko tradicijo. Strokovnjak za diagnostiko in sodobno avtomobilsko tehnologijo.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-center mb-12 after:left-1/2 after:-translate-x-1/2">Partnerstvo z Bosch</h2>
          <div className="flex flex-col md:flex-row items-start gap-8">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <img 
                src="/assets/images/o-nas/partnerstvo-z-bosch/bosch-servis.jpg" 
                alt="Bosch servis" 
                className="rounded-xl shadow-md w-full h-auto transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue border border-gray-100 card-glow"
              />
            </div>
            <div className="md:w-1/2">
              <div className="bg-white rounded-xl shadow-md p-6 transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue border border-gray-100 card-glow">
                <p className="mb-4">Leta 2002 smo se kot eni prvih v Sloveniji pridružili servisni mreži Bosch car servis. Ponosni smo na to partnerstvo, ki nam omogoča, da vam ponudimo najboljše storitve.</p>
                <p className="mb-4">Pod okriljem Bosch smo tehnično opremljeni z najsodobnejšo diagnozo. Vsako leto se udeležujemo izobraževanj o najnovejših tehnologijah v avtomobilski industriji.</p>
                <p className="mb-4">To partnerstvo zagotavlja, da lahko servisiramo vozila vseh znamk po najvišjih standardih kakovosti in z uporabo originalnih nadomestnih delov.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center p-4 bg-autoGray rounded-xl shadow-md transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue border border-gray-100 card-glow">
                    <Award className="text-autoBlue mr-3" size={24} />
                    <span className="text-lg font-medium">25+ let izkušenj</span>
                  </div>
                  <div className="flex items-center p-4 bg-autoGray rounded-xl shadow-md transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue border border-gray-100 card-glow">
                    <Users className="text-autoTeal mr-3" size={24} />
                    <span className="text-lg font-medium">Strokovno usposobljena ekipa</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;